import { forwardRef, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-medium text-metro-ink-muted"
          >
            {label}
            {props.required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={4}
          className={cn(
            "w-full resize-y rounded-md border px-3 py-2.5 text-sm text-metro-ink outline-none transition",
            "placeholder:text-metro-ink-subtle/50",
            "focus:border-metro-green-800 focus:ring-2 focus:ring-metro-green-800/10",
            error
              ? "border-red-400 focus:border-red-400 focus:ring-red-400/10"
              : "border-gray-200",
            props.disabled && "cursor-not-allowed bg-gray-50 opacity-60",
            className,
          )}
          {...props}
        />
        {hint && !error && (
          <p className="text-[11px] text-metro-ink-subtle">{hint}</p>
        )}
        {error && <p className="text-[11px] text-red-500">{error}</p>}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
export default Textarea;